import { useState } from 'react';
import { Mail, Loader2, ArrowRight, Wallet, Award, Heart } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import logo from '../assets/logo.png';

export function WelcomeScreen() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    // Instant Local Login (No emails sent)
    const { error } = await signIn(email);
    
    if (error) {
      setError(error);
      setLoading(false);
    }
    // If successful, the AuthContext will update 'session' and App.tsx will re-render
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[80px]" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo Section */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-emerald-500/10">
            <img src={logo} alt="Habl" className="w-12 h-12 object-contain drop-shadow-lg" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">Habl.Life</h1>
          <p className="text-slate-400 text-lg mb-8">Your Journey to Jannah</p>
          
          {/* Value Proposition / Mission */}
          <div className="grid grid-cols-3 gap-2 mb-8">
             <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 backdrop-blur-sm">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Heart className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">Do Good</h3>
             </div>
             <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 backdrop-blur-sm">
                <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Wallet className="w-4 h-4 text-amber-400" />
                </div>
                <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">Earn Coins</h3>
             </div>
             <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 backdrop-blur-sm">
                <div className="w-8 h-8 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Award className="w-4 h-4 text-sky-400" />
                </div>
                <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">Build Legacy</h3>
             </div>
          </div>
          
          <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed mb-8">
            Every prayer and good deed mints real <strong>Habl Coins</strong> on the blockchain. 
            Build your immutable legacy in this life and the next.
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
                Email Address
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  placeholder="name@example.com"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 p-3 rounded-xl border border-red-500/20">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 active:scale-[0.98] text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed group"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Enter <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-8">
          All data is securely stored on your device.
        </p>
      </div>
    </div>
  );
}
